import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Mail, Loader2, ArrowRight } from "lucide-react";
import STLogo from "@/components/STLogo";
import { useAuth } from "@/admin/AuthProvider";
import NoIndex from "@/admin/NoIndex";

export default function AdminLogin() {
  const { user, login, verifying } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  if (verifying) return <FullScreenLoader />;
  if (user?.role === "admin") return <Navigate to="/admin/submissions" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setErr("Email and password are required.");
      return;
    }
    setErr("");
    setSubmitting(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      if (u.role !== "admin") {
        setErr("This account does not have admin access.");
        return;
      }
      toast.success("Signed in.");
      navigate("/admin/submissions", { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Sign-in failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="admin-login-page" className="min-h-screen bg-surface-muted flex flex-col">
      <NoIndex />
      <header className="container-pro py-6 flex items-center justify-between">
        <a href="/" className="inline-flex" data-testid="admin-login-home">
          <STLogo />
        </a>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted">
          // admin · sign in
        </span>
      </header>

      <section className="flex-1 flex items-center justify-center px-4 pb-16">
        <form
          onSubmit={onSubmit}
          data-testid="admin-login-form"
          className="w-full max-w-md bg-white border border-line rounded-xl p-8 md:p-10 shadow-sm"
        >
          <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-brand-black text-white">
            <Lock size={18} />
          </div>
          <h1 className="mt-6 font-heading text-2xl md:text-3xl font-medium tracking-tight">
            Admin sign in
          </h1>
          <p className="mt-2 text-sm text-ink-secondary">
            Access the Smart Terminals inquiry inbox. Authorized personnel only.
          </p>

          <div className="mt-8 space-y-4">
            <Field id="email" icon={<Mail size={14} />} label="Email">
              <input
                id="email"
                data-testid="admin-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white border border-line rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red"
              />
            </Field>
            <Field id="password" icon={<Lock size={14} />} label="Password">
              <input
                id="password"
                data-testid="admin-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white border border-line rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red"
              />
            </Field>
          </div>

          {err && (
            <div data-testid="admin-login-error" className="mt-4 text-xs text-brand-red bg-brand-red/5 border border-brand-red/20 rounded-md px-3 py-2">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="btn-primary w-full mt-6 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? <><Loader2 size={16} className="animate-spin" /> Signing in…</> : <>Sign in <ArrowRight size={16} /></>}
          </button>

          <a href="/" className="block mt-6 text-center text-xs text-ink-muted hover:text-ink-primary" data-testid="admin-login-back">
            ← Back to website
          </a>
        </form>
      </section>
    </main>
  );
}

const Field = ({ id, label, icon, children }) => (
  <div>
    <label htmlFor={id} className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-secondary mb-1.5">
      {icon} {label}
    </label>
    {children}
  </div>
);

const FullScreenLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-surface-muted">
    <Loader2 size={20} className="animate-spin text-ink-secondary" />
  </div>
);
